import { useEffect, useState, useId } from 'react';
import { Spinner } from 'react-bootstrap';
import styles from './style.module.scss';
import { CheckedTickIcon, CrossIcon, defaultAvatar, CameraIcon } from 'assets';
import { toastMessage } from 'shared/components/toast/Toast';
import { checkFileType } from 'shared/utils/Helper';
import classNames from 'classnames';
import CropThumbnail from 'shared/modal/add_crop_thumbnail/CropThumbnail';
import ModalImage from 'shared/components/common/custom_image_lightbox/ModalImage';

const UploadAvatarArray = ({
    UploadedFile,
    avatarFileArray,
    setAvatarFileArray,
    setArray,
    edittask,
    setFile,
    selectedImg,
    setSelectedImg,
    defaultAvatarImg,
    AvatarImgStyle,
    uploadQueue = [],
    id
}) => {
    const generatedId = useId();
    const inputId = id || generatedId;
    const [profileURL, setProfileURL] = useState(typeof UploadedFile === 'string' ? UploadedFile : '');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [openCrop, setOpenCrop] = useState(false);
    const [backdrop] = useState('static');

    const MAX_FILES_PER_BATCH = 10;
    const MAX_FILE_SIZE_BYTES = 8 * 1024 * 1024; // 8 MB

    const processFiles = (rawFiles) => {
        if (!rawFiles || rawFiles.length === 0) return;

        let filesToProcess = Array.from(rawFiles);

        if (filesToProcess.length > MAX_FILES_PER_BATCH) {
            toastMessage("error", `Maximum ${MAX_FILES_PER_BATCH} images can be uploaded at once. Uploading first ${MAX_FILES_PER_BATCH}.`);
            filesToProcess = filesToProcess.slice(0, MAX_FILES_PER_BATCH);
        }

        let validFiles = [];
        for (let file of filesToProcess) {
            if (!checkFileType(file?.type)) {
                toastMessage("error", `"${file.name}" is not a supported format (Only JPG, JPEG, PNG allowed)`);
                continue;
            }
            if (file.size > MAX_FILE_SIZE_BYTES) {
                toastMessage("error", `"${file.name}" exceeds max size of 8MB`);
                continue;
            }
            validFiles.push(file);
        }

        if (validFiles.length > 0) {
            if (setFile) {
                setFile(validFiles);
                setArray(validFiles);
            }
        }
    };

    const handleImageUpload = (e) => {
        processFiles(e?.target?.files);
    };

    useEffect(() => {
        setProfileURL(typeof UploadedFile === 'string' ? UploadedFile : profileURL);
    }, [UploadedFile]);

    const removeThumbnailHandler = (e) => {
        const clone = [...avatarFileArray];
        const index = clone.indexOf(e);
        clone.splice(index, 1);
        setAvatarFileArray(clone);
    };

    const visibleAvatarFileArray = avatarFileArray.filter(
        (imageUrl) => !uploadQueue.some((queueItem) => queueItem.uploadedUrl && queueItem.uploadedUrl === imageUrl)
    );

    const renderQueueStatus = (status) => {
        if (status === 'uploading') {
            return (
                <div className={classNames(styles.StatusBadge, styles.UploadingBadge)}>
                    <Spinner animation="border" size="sm" />
                </div>
            );
        }

        if (status === 'uploaded') {
            return (
                <div className={classNames(styles.StatusBadge, styles.UploadedBadge)}>
                    <CheckedTickIcon />
                </div>
            );
        }

        if (status === 'error') {
            return (
                <div className={classNames(styles.StatusBadge, styles.ErrorBadge)}>
                    <CrossIcon />
                </div>
            );
        }

        return (
            <div className={classNames(styles.StatusBadge, styles.WaitingBadge)}>
                <Spinner animation="border" size="sm" />
            </div>
        );
    };

    const [isDragging, setIsDragging] = useState(false);

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDragEnter = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);

        processFiles(e.dataTransfer?.files);
    };

    return (
        <>
            <div
                className={classNames(
                    styles.TableFlex,
                    styles.scrollContainer,
                    isDragging && styles.DragOverActive
                )}
                onDragOver={handleDragOver}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                style={isDragging ? { border: '2.5px dashed #3b82f6', borderRadius: '12px', padding: '16px', background: 'rgba(59, 130, 246, 0.04)', transition: 'all 0.25s ease' } : { transition: 'all 0.25s ease' }}
            >
                {visibleAvatarFileArray.length > 0 &&
                    visibleAvatarFileArray.map((item, index) => {
                        return (
                            <div key={`${item}-${index}`} className={`p-0  ${styles.Avatar}`} role={'button'}>
                                <label className={classNames('position-relative', styles.UploadAvatarContainer, styles.ThumbnailFrame)} role={'button'}>
                                    <div
                                        className={styles.RedDot}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            e.preventDefault();
                                            removeThumbnailHandler(item);
                                        }}
                                        style={{ zIndex: 10 }}
                                    >
                                        x
                                    </div>
                                    <ModalImage 
                                        small={item} 
                                        large={item} 
                                        alt="wisetech" 
                                        className={classNames(AvatarImgStyle && AvatarImgStyle, `${styles.signAvatarImgStyle}`)} 
                                        imageArray={visibleAvatarFileArray}
                                        currentIndex={index}
                                    />
                                </label>
                            </div>
                        );
                    })}

                {uploadQueue.length > 0 &&
                    uploadQueue.map((item) => {
                        return (
                            <div key={item.id} className={`p-0 ${styles.Avatar}`} role={'button'}>
                                <div className={classNames('position-relative', styles.UploadAvatarContainer, styles.ThumbnailFrame, styles.QueueItem)}>
                                    <img
                                        src={item.preview}
                                        alt="wisetech"
                                        className={classNames(
                                            AvatarImgStyle && AvatarImgStyle,
                                            `${styles.signAvatarImgStyle}`,
                                            item.status === 'error' && styles.PendingErrorImage
                                        )}
                                    />
                                    <div className={styles.StatusOverlay}>{renderQueueStatus(item.status)}</div>
                                </div>
                            </div>
                        );
                    })}
                {/* -------------------------------------------------------------------------------------------- */}
                <div className={`p-0  ${styles.Avatar}`} role={'button'}>
                    <label htmlFor={inputId} className={classNames('position-relative', styles.UploadAvatarContainer, styles.ThumbnailFrame)} role={'button'}>
                        <img src={defaultAvatar} alt="wisetech" className={classNames(AvatarImgStyle && AvatarImgStyle, `${styles.signAvatarImgStyle}`)} />

                        <div className={`${styles.cameraIconStyleContainer}`} role={'button'}>
                            <CameraIcon />
                        </div>
                    </label>
                    <input
                        type="file"
                        name={inputId}
                        id={inputId}
                        role="button"
                        multiple   // ✅ allow multiple
                        className={`${styles.proilePhotoInput}`}
                        onClick={(e) => {
                            e.target.value = null;
                        }}
                        accept="image/*"
                        onChange={(e) => {
                            handleImageUpload(e);
                        }}
                    />

                    {UploadedFile && (
                        <CropThumbnail
                            show={isModalOpen}
                            setOpen={setIsModalOpen}
                            UploadedFile={UploadedFile}
                            uploadedPhotoUrl={profileURL}
                            setUploadedPhotoURL={setProfileURL}
                            setOpenCrop={setOpenCrop}
                            cropShape={'rect'}
                            aspect={1}
                            setArray={setArray}
                            setFile={setFile}
                            handleClose={() => setIsModalOpen(false)}
                            setSelectedImage={setSelectedImg}
                            backdrop={backdrop}
                            status="uploadArray"
                        />
                    )}
                </div>
            </div>
        </>
    );
};
export default UploadAvatarArray;
